<script setup lang="ts">
  import { computed } from 'vue';
  import { Archive, Monitor } from 'lucide-vue-next';
  import { SelectInput } from '@/shared/ui';
  import { ELECTRONIC_SYSTEM_NAME, MAX_PHYSICAL_LOCATIONS } from '../../lib/record-config-options';
  import RecordConfigRepeater from './RecordConfigRepeater.vue';
  import type { RecordConfigOption } from '../../api/record-config-options.api';

  const EMPTY_LOCATIONS_MESSAGE = 'Nenhum local físico cadastrado.';

  const props = defineProps<{
    showElectronicBlock: boolean;
    showPhysicalBlock: boolean;
    physicalLocationIds: Array<number | null>;
    locations: RecordConfigOption[];
    isLoading: boolean;
  }>();

  const emit = defineEmits<{
    'update:physicalLocationIds': [value: Array<number | null>];
  }>();

  const hasNoLocations = computed(() => props.locations.length === 0 && !props.isLoading);

  function optionsFor(index: number, ownValue: number | null): RecordConfigOption[] {
    const takenElsewhere = new Set(
      props.physicalLocationIds.filter((value, rowIndex) => rowIndex !== index && value !== null)
    );

    return props.locations.filter(({ id }) => id === ownValue || !takenElsewhere.has(id));
  }

  function toRowValue(value: string | number): number | null {
    return value === '' ? null : Number(value);
  }
</script>

<template>
  <section class="flex flex-col gap-4 px-8 py-6" data-testid="record-config-storage-section">
    <span class="text-sm font-semibold text-gray-07">Armazenamento:</span>

    <div
      v-if="showElectronicBlock"
      class="flex flex-col gap-2"
      data-testid="record-config-storage-electronic-block"
    >
      <div class="flex items-center gap-2 text-sm font-semibold text-gray-07">
        <Monitor class="size-4 text-[var(--theme-secondary-ink)]" aria-hidden="true" />
        <span>Sistema Eletrônico</span>
      </div>
      <div
        class="flex h-12 w-72 max-w-full items-center rounded-lg border border-gray-02 bg-gray-02/20 px-5 text-sm text-gray-07"
        data-testid="record-config-storage-electronic"
      >
        {{ ELECTRONIC_SYSTEM_NAME }}
      </div>
    </div>

    <div
      v-if="showPhysicalBlock"
      class="flex flex-col gap-2"
      data-testid="record-config-storage-physical-block"
    >
      <div class="flex items-center gap-2 text-sm font-semibold text-gray-07">
        <Archive class="size-4 text-[var(--theme-secondary-ink)]" aria-hidden="true" />
        <span>Meio físico</span>
      </div>

      <p
        v-if="hasNoLocations"
        class="text-sm text-gray-05"
        data-testid="record-config-storage-physical-empty"
      >
        {{ EMPTY_LOCATIONS_MESSAGE }}
      </p>

      <RecordConfigRepeater
        v-else
        :model-value="physicalLocationIds"
        :empty-value="null"
        :max="MAX_PHYSICAL_LOCATIONS"
        add-label="Adicionar local físico"
        remove-label="Remover local físico"
        @update:model-value="emit('update:physicalLocationIds', $event)"
      >
        <template #default="{ value, index, update }">
          <SelectInput
            :id="`record-config-location-${index}`"
            :model-value="value ?? ''"
            :options="optionsFor(index, value)"
            label="Selecione"
            :auto-select-first="false"
            :disabled="isLoading"
            container-class="w-72 max-w-full"
            @update:model-value="update(toRowValue($event))"
          />
        </template>
      </RecordConfigRepeater>
    </div>
  </section>
</template>
